import { useEffect, useState } from 'react';
import {
  Box, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, MenuItem, Typography, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import TagSelect from './TagSelect';
import type { BulkResult, TagMode } from '../utils/entity-list';

export type BulkFieldType = 'tags' | 'status' | 'number' | 'select';

export interface BulkFieldDef {
  /** Update-payload key (e.g. 'status', 'traffic_cap_limit'); 'tag_ids' for tags. */
  key: string;
  label: string;
  type: BulkFieldType;
  /** For type 'select'. An empty-string value is sent as null, so a field can be cleared. */
  options?: { value: string; label: string }[];
  helperText?: string;
}

const STATUS_OPTIONS = ['active', 'archived'];

interface Props {
  open: boolean;
  count: number;
  fields: BulkFieldDef[];
  loading?: boolean;
  onClose: () => void;
  onApply: (result: BulkResult) => void;
}

/**
 * Bulk-edit dialog: each field has an enable-checkbox and only enabled fields are
 * written to the selected rows (ar-venus BulkUpdateOffersModal pattern). Tags get
 * a Set / Add / Remove mode so bulk-tagging doesn't wipe existing tags.
 */
export default function BulkEditDialog({ open, count, fields, loading, onClose, onApply }: Props) {
  const [enabled, setEnabled] = useState<Record<string, boolean>>({});
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [tagMode, setTagMode] = useState<TagMode>('add');
  const [status, setStatus] = useState('active');
  const [nums, setNums] = useState<Record<string, string>>({});
  const [selects, setSelects] = useState<Record<string, string>>({});

  // Reset every time the dialog opens.
  useEffect(() => {
    if (open) {
      setEnabled({});
      setTagIds([]);
      setTagMode('add');
      setStatus('active');
      setNums({});
      setSelects({});
    }
  }, [open]);

  const toggle = (key: string) => setEnabled((e) => ({ ...e, [key]: !e[key] }));

  const anyEnabled = fields.some((f) => enabled[f.key]);

  const handleApply = () => {
    const updates: Record<string, unknown> = {};
    let tags: BulkResult['tags'];
    for (const f of fields) {
      if (!enabled[f.key]) continue;
      if (f.type === 'tags') tags = { mode: tagMode, ids: tagIds };
      else if (f.type === 'status') updates[f.key] = status;
      else if (f.type === 'number') updates[f.key] = Number(nums[f.key] ?? 0);
      // '' means "clear it" — send null rather than an empty string so the column nulls out.
      else if (f.type === 'select') updates[f.key] = selects[f.key] ? selects[f.key] : null;
    }
    onApply({ updates, tags });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Bulk edit {count} {count === 1 ? 'item' : 'items'}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Tick a field to change it on all selected items. Unticked fields are left untouched.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {fields.map((f) => (
            <Box key={f.key} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <Checkbox
                size="small"
                checked={!!enabled[f.key]}
                onChange={() => toggle(f.key)}
                disabled={loading}
                sx={{ mt: f.type === 'tags' ? 0.5 : 0 }}
              />
              <Box sx={{ flex: 1 }}>
                {f.type === 'tags' && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={tagMode}
                      onChange={(_, v) => v && setTagMode(v)}
                      disabled={!enabled[f.key] || loading}
                    >
                      <ToggleButton value="add">Add</ToggleButton>
                      <ToggleButton value="remove">Remove</ToggleButton>
                      <ToggleButton value="set">Replace</ToggleButton>
                    </ToggleButtonGroup>
                    <TagSelect
                      value={tagIds}
                      onChange={setTagIds}
                      label={f.label}
                      allowCreate
                    />
                  </Box>
                )}
                {f.type === 'status' && (
                  <TextField
                    select fullWidth size="small" label={f.label} value={status}
                    onChange={(e) => setStatus(e.target.value)} disabled={!enabled[f.key] || loading}
                  >
                    {STATUS_OPTIONS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </TextField>
                )}
                {f.type === 'select' && (
                  <TextField
                    select fullWidth size="small" label={f.label}
                    value={selects[f.key] ?? ''}
                    onChange={(e) => setSelects((v) => ({ ...v, [f.key]: e.target.value }))}
                    disabled={!enabled[f.key] || loading}
                    helperText={f.helperText}
                  >
                    <MenuItem value=""><em>(none — clear it)</em></MenuItem>
                    {(f.options || []).map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                  </TextField>
                )}
                {f.type === 'number' && (
                  <TextField
                    fullWidth size="small" type="number" label={f.label}
                    value={nums[f.key] ?? ''} onChange={(e) => setNums((n) => ({ ...n, [f.key]: e.target.value }))}
                    disabled={!enabled[f.key] || loading}
                    helperText={f.key.includes('cap') ? '0 = unlimited' : undefined}
                  />
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button variant="contained" onClick={handleApply} disabled={!anyEnabled || loading}>
          {loading ? 'Applying…' : 'Apply'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
